import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, X, Trash2, Lock, Loader2, ShieldCheck } from "lucide-react";
import Crest from "./Crest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hh_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [entries, setEntries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("pending");

  const login = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password: pwd });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (e) {
      setErr("Invalid email or password.");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [g, cm] = await Promise.all([
        axios.get(`${API}/admin/guestbook`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/contact`, { headers: { "X-Admin-Token": token } }),
      ]);
      setEntries(g.data);
      setContacts(cm.data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(""); }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const moderate = async (id, action) => {
    await axios.patch(`${API}/admin/guestbook/${id}`, { action }, { headers: { "X-Admin-Token": token } });
    load();
  };
  const remove = async (id) => {
    await axios.delete(`${API}/admin/guestbook/${id}`, { headers: { "X-Admin-Token": token } });
    load();
  };
  const removeContact = async (id) => {
    await axios.delete(`${API}/admin/contact/${id}`, { headers: { "X-Admin-Token": token } });
    load();
  };
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };

  if (!token) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-5">
        <form onSubmit={login} className="w-full max-w-sm text-center">
          <Crest size={56} className="mx-auto" />
          <h1 className="font-display text-4xl text-parchment mt-6">Admin</h1>
          <p className="text-stone2 font-light mt-2">Guestbook moderation</p>
          <div className="mt-8 flex items-center gap-2 border-b border-parchment/30 focus-within:border-gold">
            <Lock className="w-4 h-4 text-stone2" />
            <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="flex-1 bg-transparent outline-none py-3 text-xl font-display text-parchment placeholder:text-parchment/35" />
          </div>
          <div className="mt-5 flex items-center gap-2 border-b border-parchment/30 focus-within:border-gold">
            <Lock className="w-4 h-4 text-stone2" />
            <input data-testid="admin-password-input" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Password" className="flex-1 bg-transparent outline-none py-3 text-xl font-display text-parchment placeholder:text-parchment/35" />
          </div>
          {err && <p className="text-oxblood text-sm mt-3" data-testid="admin-login-error">{err}</p>}
          <button type="submit" data-testid="admin-login-button" className="mt-8 w-full font-label text-[0.72rem] uppercase tracking-[0.22em] text-ink bg-gold py-4 hover:opacity-90 transition-opacity">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const filtered = entries.filter((e) => (tab === "all" ? true : e.status === tab));
  const counts = {
    pending: entries.filter((e) => e.status === "pending").length,
    approved: entries.filter((e) => e.status === "approved").length,
    all: entries.length,
    messages: contacts.length,
  };

  return (
    <div className="min-h-screen bg-ink pt-28 pb-20">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-gold" />
            <h1 className="font-display text-4xl text-parchment">Guestbook Admin</h1>
          </div>
          <button onClick={logout} className="font-label text-[0.62rem] uppercase tracking-[0.18em] text-stone2 hover:text-gold border border-[var(--c-border)] px-4 py-2">Log out</button>
        </div>

        <div className="flex gap-2 mt-8">
          {["pending", "approved", "all", "messages"].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`font-label text-[0.62rem] uppercase tracking-[0.16em] px-4 py-2 border transition-colors ${tab === t ? "bg-gold text-ink border-gold" : "text-stone2 border-[var(--c-border)] hover:text-gold"}`}>
              {t} ({counts[t] ?? 0})
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-stone2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : tab === "messages" ? (
            contacts.length === 0 ? (
              <p className="text-parchment/50 font-light italic py-10">No contact messages yet.</p>
            ) : (
              contacts.map((m) => (
                <div key={m.id} data-testid={`admin-message-${m.id}`} className="border border-[var(--c-border)] bg-ink2 p-6 flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex-1">
                    <p className="font-display text-xl text-parchment leading-relaxed whitespace-pre-wrap">{m.message}</p>
                    <div className="mt-3 font-label text-[0.62rem] uppercase tracking-[0.16em] text-gold">
                      {m.name} · <a href={`mailto:${m.email}`} className="hover:underline lowercase tracking-normal">{m.email}</a>{m.phone ? ` · ${m.phone}` : ""}
                    </div>
                  </div>
                  <button onClick={() => removeContact(m.id)} data-testid={`admin-message-delete-${m.id}`} title="Delete" className="grid place-items-center w-10 h-10 border border-[var(--c-border)] text-stone2 hover:text-parchment shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))
            )
          ) : filtered.length === 0 ? (
            <p className="text-parchment/50 font-light italic py-10">No entries in “{tab}”.</p>
          ) : (
            filtered.map((en) => (
              <div key={en.id} className="border border-[var(--c-border)] bg-ink2 p-6 flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex-1">
                  <p className="font-display text-xl text-parchment leading-relaxed">“{en.message}”</p>
                  <div className="mt-2 font-label text-[0.62rem] uppercase tracking-[0.16em] text-gold">
                    {en.name}{en.location ? ` · ${en.location}` : ""}
                    <span className={`ml-3 ${en.status === "approved" ? "text-moss" : en.status === "rejected" ? "text-oxblood" : "text-stone2"}`}>[{en.status}]</span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {en.status !== "approved" && (
                    <button onClick={() => moderate(en.id, "approve")} title="Approve" className="grid place-items-center w-10 h-10 border border-[var(--c-border)] text-moss hover:bg-moss/10"><Check className="w-4 h-4" /></button>
                  )}
                  {en.status !== "rejected" && (
                    <button onClick={() => moderate(en.id, "reject")} title="Reject" className="grid place-items-center w-10 h-10 border border-[var(--c-border)] text-oxblood hover:bg-oxblood/10"><X className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => remove(en.id)} title="Delete" className="grid place-items-center w-10 h-10 border border-[var(--c-border)] text-stone2 hover:text-parchment"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
